// WHAT THE OWNER WAS SHOWN BEFORE AN ACT THE SERVER NOW KEEPS, BY HASH (R-pending-ack-retention; contract 2.31.0).
//
// Three acts send the words their screen showed, each as the SHA-256 of the sentence the page holds — the house consent
// pattern (`consentHash.ts`): what is hashed is the held sentence's own text, never something composed around it.
//   - P3, the spend: the sheets in front of the money (`spend` `shownCopy`) — the compass sheet when the owner confirmed the
//     address there, the purchase notice and the route chosen when it was shown, and the credit pop-up; and, by the same
//     rule, the protest-window notice (T-1) the pop-up rendered — the SERVER's sentence, which the server matches against
//     its own T-1 (as stored, or worked out again at the spend) and never takes as words (Fable review P2-a);
//   - P5, the Terms checkbox (`assent` `checkboxSha256`): the 18-or-older sentence ticked;
//   - P6, the delete warning (`deleteAccount` `shownWarnings`): the lines the screen showed before "Delete".
// A SENTENCE WITH A SLOT IS HASHED WITH ITS SLOT ("…for {address} — start to finish."): the filled-in line holds the owner's
// address, which no kept record may. The server keeps only hashes it issued (functions/src/ackCopy.ts, pinned to these
// texts by web/test/ackCopy.test.ts), counts any other, and never refuses the act over one — so nothing here can stop a
// spend, a sign-up or a deletion. No Web Crypto: no hashes, and the act goes ahead with none.

import { prePayText } from "../components/prePayCopy";
import { heldText } from "../components/heldCopy";
import { afterText } from "../components/afterFilingCopy";
import { words } from "../routes/steps/detailsWording";
import type { PurchaseNoticeChoice } from "./purchaseNotice";
import { textSha256 } from "./consentHash";

/** What the spend's sheets put in front of the owner, as this page knows it at the moment of "Use my credit". */
export interface SpendSheetFacts {
  /** The address the pop-up names, or null when it names none (`confirm.coversNoAddress`). */
  readonly address: string | null;
  /** The unit line, when the pop-up shows one. */
  readonly unit: string | null;
  /** The directional warning line (G-3's `directionalConflict`). */
  readonly directional: boolean;
  /** The owner pressed "Use {address} anyway" on the compass sheet. */
  readonly compassConfirmed: boolean;
  /** The purchase notice's route the owner chose, when the notice was shown and answered. This tab's answer for the appeal
   *  (`app/purchaseNotice.ts` `held`), which may come from an EARLIER visit to `/checkout` in the same tab — a notice answered,
   *  then "Not yet", then "Use a credit and start" again — so it says the owner answered that notice, not that it was the
   *  last thing on the screen. A full-page trip to Stripe empties it. */
  readonly purchaseAnswer: Exclude<PurchaseNoticeChoice, "goBack"> | null;
  /** The text of T-1's notice the pop-up rendered (`CopyLine` of the resolution's T-1), or null when it rendered none. */
  readonly protestNotice: string | null;
}

const ROUTE_TITLE = {
  saleIsntRight: "details.purchase.wrongSale.title",
  notOpenMarket: "details.purchase.notMarket.title",
  otherGrounds: "details.grounds.title",
} as const;

/** The held sentences the sheets showed, slots unfilled, GROUPED BY SHEET in a fixed order — compass, purchase notice,
 *  credit pop-up — which is NOT necessarily the order the owner met them (Fable review P3-g): the compass sheet comes
 *  before the pop-up when the server's answer already held the spend for the address, and after it when the spend itself
 *  was refused for the address (a 409, then "Use {address} anyway"). The server keeps them as a set. Pure. */
export function spendSheetTexts(f: SpendSheetFacts): string[] {
  const out: string[] = [];
  if (f.compassConfirmed) {
    out.push(prePayText("compass.title"), prePayText("compass.body"), prePayText("compass.different"), prePayText("compass.anyway"));
  }
  if (f.purchaseAnswer !== null) {
    out.push(words("details.purchase.title"), heldText("purchaseNotice.otherGrounds"), words(ROUTE_TITLE[f.purchaseAnswer]));
  }
  out.push(prePayText("confirm.title"), prePayText(f.address === null ? "confirm.coversNoAddress" : "confirm.covers"));
  if (f.unit !== null) out.push(prePayText("confirm.unit"));
  if (f.directional) out.push(prePayText("confirm.directional"));
  out.push(prePayText("confirm.m3"), prePayText("confirm.use"));
  return out;
}

/** The SERVER's sentences the pop-up rendered, as the page received them: T-1's notice. Sent by hash beside the held ones;
 *  the server marks its own matching sentence `shown` and keeps no text. Pure. */
export function spendServedTexts(f: SpendSheetFacts): string[] {
  return f.protestNotice === null ? [] : [f.protestNotice];
}

/** The delete warning as the screen showed it: the credits line (when the account held credits), the footer under
 *  "Delete account", and the confirm line — in the one-account words or today's, as `DeleteAccount.tsx` chose. Pure. */
export function deleteWarningTexts(f: { readonly oneAccount: boolean; readonly credits: number }): string[] {
  const out: string[] = [];
  if (f.credits > 0) out.push(heldText(f.oneAccount ? "account.k.deleteRemovesEverywhere" : "account.delete.unusedCredits"));
  out.push(f.oneAccount ? heldText("account.k.deleteFooter") : afterText("account.delete.footer"));
  out.push(heldText(f.oneAccount ? "account.k.deleteConfirm" : "account.delete.confirm"));
  return out;
}

/** Each text's SHA-256, in order, without repeats; a text the browser cannot hash is left out. */
export async function hashesOf(texts: readonly string[]): Promise<string[]> {
  const out: string[] = [];
  for (const t of texts) {
    const h = await textSha256(t);
    if (h !== null && !out.includes(h)) out.push(h);
  }
  return out;
}

/** P5: the checkbox sentence's SHA-256, as the page holds it, for `assent`'s `checkboxSha256`; nothing where it cannot be
 *  hashed. The server keeps it only with ASSENT_CHECKBOX_HASH_ENABLED on and only when it issued it. */
export async function checkboxSha256(): Promise<{ checkboxSha256: string } | Record<string, never>> {
  const hash = await textSha256(heldText("assent.onboardingCheckbox"));
  return hash === null ? {} : { checkboxSha256: hash };
}
